// ParameterDisplay.js
import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import './ParameterDisplay.css'; // We will create this
import { CardHeader } from '@mui/material';

/**
 * Helper to find the first available text in any language (defaults to 'en')
 */
const getDisplayText = (textObj, selectedReportLanguageCode) => {
  if (!textObj) return "";
  if (textObj[selectedReportLanguageCode]) {
    return textObj[selectedReportLanguageCode];
  } else {
    return textObj.en;
  }
  // Fallback to the first available language
  // const firstLang = Object.keys(textObj)[0];
  // return textObj[firstLang] || "";
};

/**
 * Helper component to render a list with icons
 */
const RenderList = ({ items, iconPath = '/icons/', selectedReportLanguageCode }) => {
  if (!items || items.length === 0) return null;

  return (
    <List dense>
      {items.map((item, index) => (
        <ListItem key={index} className="list-item">
          {item.image && (
            <ListItemIcon className="list-item-icon">
              <img src={`${iconPath}${item.image}`} alt="icon" />
            </ListItemIcon>
          )}
          <ListItemText 
            primary={<span dangerouslySetInnerHTML={{ __html: getDisplayText(item.text, selectedReportLanguageCode) }} />} 
          />
        </ListItem>
      ))}
    </List>
  );
};

/**
 * Main component to display the parameter data
 */
const ParameterDisplay = ({ parameterData, selectedReportLanguageCode }) => {
  if (!parameterData) return null;

  const { text, dynamicText, tips, causes, additionalSections } = parameterData;

  const textContent = getDisplayText(text,selectedReportLanguageCode);
  const dynamicTextContent = getDisplayText(dynamicText, selectedReportLanguageCode);
  const tipsHeader = getDisplayText(tips?.tipsHeader, selectedReportLanguageCode);
  const tipsArray = tips?.tipsSvgText?.[selectedReportLanguageCode] || tips?.tipsSvgText?.en || []; // Default to 'en' array
  const tipsIcons = tips?.tipsSvg || [];

  return (
    <Box className="param-display-wrapper">

      {/* 1. Main Text Section */}
      {textContent && (
        <Card className="param-card">
          <CardContent>
          <Typography variant="h6" component="h3" gutterBottom>Parameter Discription</Typography>
            <Typography variant="body1" dangerouslySetInnerHTML={{ __html: textContent }} />
          </CardContent>
        </Card>
      )}

      {/* 2. Dynamic Text Section */}
      {dynamicTextContent && (
        <Card className="param-card">
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              Details
            </Typography>
            <Typography variant="body1" dangerouslySetInnerHTML={{ __html: dynamicTextContent }} />
          </CardContent>
        </Card>
      )}

      {/* 3. Tips Section */}
      {(tipsHeader || tipsArray.length > 0) && (
        <Card className="param-card">
          <CardContent>
            {tipsHeader && (
              <Typography variant="h6" component="h3" gutterBottom>
                {tipsHeader}
              </Typography>
            )}
            <List dense>
              {tipsArray.map((tipText, index) => (
                <ListItem key={index} className="list-item">
                  {tipsIcons[index] && (
                    <ListItemIcon className="list-item-icon">
                      <img src={`https://reportsvg.s3.ap-south-1.amazonaws.com/reportSvg/pfh/svg/${tipsIcons[index]}`} alt="icon" />
                    </ListItemIcon>
                  )}
                  <ListItemText primary={tipText} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* 4. Causes Section */}
      {causes && Object.entries(causes).map(([key, causeData]) => (
        <Card key={key} className="param-card">
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              {getDisplayText(causeData.heading, selectedReportLanguageCode)}
            </Typography>
            {getDisplayText(causeData.description, selectedReportLanguageCode) && (
              <Typography variant="body2" color="text.secondary" paragraph>
                {getDisplayText(causeData.description, selectedReportLanguageCode)}
              </Typography>
            )}
            <RenderList items={causeData.list} selectedReportLanguageCode={selectedReportLanguageCode} />
          </CardContent>
        </Card>
      ))}

      {/* 5. Additional Sections */}
      {additionalSections && additionalSections.map((section, index) => (
        <Card key={index} className="param-card">
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              {getDisplayText(section.heading, selectedReportLanguageCode)}
            </Typography>
            {getDisplayText(section.description, selectedReportLanguageCode) && (
              <Typography variant="body2" color="text.secondary" paragraph>
                {getDisplayText(section.description, selectedReportLanguageCode)}
              </Typography>
            )}
            <RenderList items={section.list} selectedReportLanguageCode={selectedReportLanguageCode} />
          </CardContent>
        </Card>
      ))}

    </Box>
  );
};

export default ParameterDisplay;